/**
 * 有償機能アクティベート情報のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'pages';
import {
  beginFetch,
  endFetch,
  failedFetch,
  FetchStatus
} from 'common/modules/app/fetch';
import {
  fetchFailure,
  ErrorResponse,
  clearErrors
} from 'common/modules/app/errors';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import {
  getDevicesActivationRequest,
  postDevicesActivationReserveRequest,
  putDevicesActivationOutputRequest,
  postDevicesActivationReserveCancelRequest
} from 'common/utils/request/sas-request';
import {
  GetDevicesActivationResponse,
  PostDevicesActivationReserveResponse,
  PutDevicesActivationOutputResponse,
  ActivationData,
  ActivationHeader
} from 'common/utils/request/sas-response';
import { request } from 'common/utils/request/http-request';
import { outputACTFile } from 'common/utils/file';
import { BoardStatus, setIsProcessing, setStatus } from '../app/board';
import { postAccessToken } from 'common/utils/token';
import {
  deleteCookie,
  loadCookie,
  SasCookieKeys
} from 'common/utils/cookie-utils';
import { loggedOut } from '../app/principal';
import { BoardGL, Glossary } from 'common/utils/glossary';
import {
  getPurchasesHistory,
  reset,
  StateType as PurchaseStateType,
  SortType
} from './purchase';

enum ActionTypes {
  RESET_STATE = 'domain/device_activate/RESET_STATE',
  FETCH_SUCCESS = 'domain/device_activate/FETCH_SUCCESS',
  CHANGE_SETTING = 'domain/device_activate/CHANGE_SETTING',
  SET_REQ_DATA_LIST = 'domain/device_activate/SET_REQ_DATA_LIST',
  SET_RESERVE_RESPONSE = 'domain/device_activate/SET_RESERVE_RESPONSE',
  SET_FILE_LOADING_ERROR_DATA = 'domain/device_activate/SET_FILE_LOADING_ERROR_DATA',
  SET_API_ERROR_MESSAGE = 'domain/device_activate/SET_API_ERROR_MESSAGE',
  SET_DIALOG_MODE = 'domain/device_activate/SET_DIALOG_MODE'
}

export enum StateType {
  // 有効
  TYPE_ACTIVE = 'active',
  // 予約
  TYPE_RESERVE = 'reserve'
}

export enum DialogType {
  // 通常
  NORMAL = 'normal',
  // 選択削除
  DELETE = 'delete'
}

export const GET_DEVICES_ACTIVATION_ID = 'get_devices_activation';
export const POST_DEVICES_ACTIVATION_RESERVE_ID =
  'post_devices_activation_reserve';
export const GET_DEVICES_ACTIVATION_OUTPUT_ID = 'get_devices_activation_output';
export const POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID =
  'post_devices_activation_reserve_cancel';

/**
 * 有償機能アクティベート情報をクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * 有償機能アクティベート情報を置き換える
 */
export const fetchSuccess = (
  purchaseId: string,
  payload: GetDevicesActivationResponse
) => {
  // GetDevicesActivationResponseをDeviceActivateStateに変換してActionを作成
  const deviceActivateState: DeviceActivateState = {};
  deviceActivateState[purchaseId] = {
    usage: payload.usage,
    limit: payload.limit,
    confirmAt: payload.confirmAt,
    activates: {}
  };
  for (const data of payload.activates) {
    // 端末ID(機種ID+シリアル番号)を生成(このidがユニークな値になるため、payload.activates内に重複するデータがある場合は最初のデータで上書きして、以降の重複データは無視する)
    const id = data.modelId + data.serialNo;
    deviceActivateState[purchaseId].activates[id] = {
      deviceActivateInfo: data,
      isDelete: false
    };
  }
  return createAction(ActionTypes.FETCH_SUCCESS, deviceActivateState);
};

/**
 * 削除対象か否かのフラグを更新
 * @param purchaseId 購入ID
 * @param terminalId 端末ID
 * @param isDelete 削除フラグ
 */
export const changeSetting = (
  purchaseId: string,
  terminalId: string,
  isDelete: boolean
) => {
  return createAction(ActionTypes.CHANGE_SETTING, {
    purchaseId: purchaseId,
    terminalId: terminalId,
    isDelete: isDelete
  });
};

/**
 * リクエストファイルをアップロードして読み込みに成功した結果を保持
 * */
export const setReqFileDataList = (payload: []) => {
  return createAction(ActionTypes.SET_REQ_DATA_LIST, payload);
};

/**
 * 有償ファームウェアのアクティベート予約APIのレスポンスを保持
 */
export const setReserveResponse = (
  payload: PostDevicesActivationReserveResponse
) => {
  return createAction(ActionTypes.SET_RESERVE_RESPONSE, payload);
};

/**
 * リクエストファイル読み込み時のエラーメッセージを保持
 */
export const setFileLoadingErrorData = (payload: FileLoadingErrorInfo[]) => {
  return createAction(ActionTypes.SET_FILE_LOADING_ERROR_DATA, payload);
};

/**
 * 予約API/確定APIのエラーメッセージを保持
 */
export const setApiErrorMessage = (payload: string) => {
  return createAction(ActionTypes.SET_API_ERROR_MESSAGE, payload);
};

/** ダイアログのモードを切り替え */
export const setDialogMode = (payload: DialogType) => {
  return createAction(ActionTypes.SET_DIALOG_MODE, payload);
};

const actions = {
  resetState,
  fetchSuccess,
  changeSetting,
  setReqFileDataList,
  setReserveResponse,
  setFileLoadingErrorData,
  setApiErrorMessage,
  setDialogMode
};
type TerminalAction = ActionsUnion<typeof actions>;

/**
 * 有償機能アクティベート情報一覧を取得する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 購入番号
 * @param isRetry: 再実施か否か
 * @param isAutoRetryOnError: 2秒毎に15回リトライを実施するか否か(有償ファームウェアのアクティベート確定APIを呼び出した後の再取得用(iOSのダウンロードダイアログ対策))
 * @param _autoRetryCount: isAutoRetryOnErrorがtrueの時にリトライ回数をカウントする用(getDevicesActivationの中でのみ値を指定する、他はデフォルトの0となる)
 */
export const getDevicesActivation = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  isRetry?: boolean,
  isAutoRetryOnError?: boolean,
  _autoRetryCount = 0
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義 nextKeyで取得する場合は必要
    const isCancel = () => {
      const status = getState().app.fetch[GET_DEVICES_ACTIVATION_ID];
      return status === FetchStatus.CANCEL;
    };
    // GET_DEVICES_ACTIVATION_IDを処理中に変更
    dispatch(beginFetch(GET_DEVICES_ACTIVATION_ID));
    dispatch(setIsProcessing(true));

    // ErrorStateが有償機能アクティベート情報取得だった場合はリセットする
    if (getState().app.error.requestId === GET_DEVICES_ACTIVATION_ID) {
      dispatch(clearErrors());
    }

    // レスポンスの値で更新する
    let nextKey = '';
    const activateList: DeviceActivateInformation[] = [];
    const result: GetDevicesActivationResponse = {
      usage: 0,
      limit: 0,
      confirmAt: '',
      activates: activateList,
      nextKey: nextKey
    };

    do {
      //有償ファームウェアのアクティベート一覧取得APIを実行
      const requestData = getDevicesActivationRequest(
        token,
        purchaseId,
        nextKey
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = GET_DEVICES_ACTIVATION_ID;
        // 403エラーの場合はトークン更新後に再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              await dispatch(
                getDevicesActivation(
                  cookie[SasCookieKeys.KEY_TOKEN],
                  cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                  purchaseId,
                  true,
                  isAutoRetryOnError,
                  _autoRetryCount
                )
              );
              // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも401or403だった時はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403(needLogout)であった場合はSASのみログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        } else {
          // 401/403以外の場合且つisAutoRetryOnErrorがtrue且つ_autoRetryCountが15未満の場合リトライを行う
          if (isAutoRetryOnError && _autoRetryCount < 15) {
            // 2秒間待機
            await new Promise((resolve) => setTimeout(resolve, 2000));
            // cookieの情報を取得
            const cookie = loadCookie();
            // 有償機能アクティベート情報一覧を取得を再実行
            await dispatch(
              getDevicesActivation(
                cookie[SasCookieKeys.KEY_TOKEN],
                cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                purchaseId,
                isRetry,
                true,
                _autoRetryCount + 1
              )
            );
            return;
          }
        }
        // エラー時の共通処理
        dispatch(fetchFailure(error));
        dispatch(failedFetch(GET_DEVICES_ACTIVATION_ID));
        dispatch(setIsProcessing(false));
        return;
      }
      // APIが成功した場合は取得した端末情報でstateを更新
      const body: GetDevicesActivationResponse = (response.body as unknown) as GetDevicesActivationResponse;

      // 購入プランの利用実績情報から取り出した情報を保持
      result.usage = body.usage;
      result.limit = body.limit;
      result.confirmAt = body.confirmAt;
      // カメラ有償アクティベート情報から取り出した情報を保持(追加していく)
      for (const data of body.activates) {
        activateList.push(data);
      }

      // nextKeyの有無、キャンセルの有無を判断
      if (body.nextKey === '' || isCancel()) {
        // キャンセル又は次の取得が無いため処理を抜ける
        nextKey = '';
      } else {
        // nextKeyを更新
        nextKey = body.nextKey;
      }
    } while (nextKey);
    // 取得した端末情報でstateを更新
    dispatch(fetchSuccess(purchaseId, result));
    dispatch(setIsProcessing(false));
    // GET_DEVICES_ACTIVATION_IDを未処理に変更
    dispatch(endFetch(GET_DEVICES_ACTIVATION_ID));
  };
};

/**
 * 有償ファームウェアのアクティベート予約APIを実行する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 購入番号
 * @param reqDatas: REQデータリスト([{ name: file.name, data: parsedData }])
 * @param glossary: グロッサリ
 * @param language: 言語
 * @param isRetry: 再実施か否か
 * @param isDryRun: 予約APIの実行モード(DryRynモード:true、通常モード:false)
 */
export const postDevicesActivationReserve = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  reqDatas: [],
  glossary: Glossary,
  language: string,
  isRetry?: boolean,
  isDryRun?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義
    const isCancel = () => {
      const status = getState().app.fetch[POST_DEVICES_ACTIVATION_RESERVE_ID];
      return status === FetchStatus.CANCEL;
    };
    // POST_DEVICES_ACTIVATION_RESERVE_IDを処理中に変更
    dispatch(beginFetch(POST_DEVICES_ACTIVATION_RESERVE_ID));
    dispatch(setIsProcessing(true));

    // ErrorStateが有償機能予約だった場合はリセットする
    if (getState().app.error.requestId === POST_DEVICES_ACTIVATION_RESERVE_ID) {
      dispatch(clearErrors());
    }
    // REQデータリストを5件ずつに分割する
    function chunk<T extends any[]>(arr: T, size: number) {
      return arr.reduce(
        (newarr, _, i) =>
          i % size ? newarr : [...newarr, arr.slice(i, i + size)],
        []
      );
    }
    const chunkList = chunk(reqDatas, 5);

    // 結果の一時保持用
    const results: PostDevicesActivationReserveResponse = {
      successDevices: [],
      warningDevices: [],
      errorDevices: []
    };
    // 有償ファームウェアのアクティベート予約APIを5件ずつ実行する
    for (const datas of chunkList) {
      // 有償ファームウェアのアクティベート予約APIを実行
      const requestData = postDevicesActivationReserveRequest(
        token,
        purchaseId,
        datas,
        isDryRun,
        language
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = POST_DEVICES_ACTIVATION_RESERVE_ID;
        // 403エラーの場合はトークン更新後に再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              await dispatch(
                postDevicesActivationReserve(
                  cookie[SasCookieKeys.KEY_TOKEN],
                  cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                  purchaseId,
                  reqDatas,
                  glossary,
                  language,
                  true,
                  isDryRun
                )
              );
              // 最終的な結果は再帰側になるので、最初の呼び出しは完了する
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも401or403だった場合はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403(needLogout)であった場合はSASのみログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(POST_DEVICES_ACTIVATION_RESERVE_ID));
        dispatch(setIsProcessing(false));
        // エラーダイアログを表示
        dispatch(setApiErrorMessage(glossary.glx(BoardGL.UploadFailure)));
        dispatch(
          setStatus(BoardStatus.DEVICE_ACTIVATE_API_ERROR_COMPLETION_DISP)
        );
        return;
      }
      // APIが成功した場合は一時的に結果を保持する
      const body: PostDevicesActivationReserveResponse = (response.body as unknown) as PostDevicesActivationReserveResponse;

      for (const success of body.successDevices) {
        results.successDevices.push(success);
      }
      for (const warning of body.warningDevices) {
        results.warningDevices.push(warning);
      }
      for (const error of body.errorDevices) {
        results.errorDevices.push(error);
      }
      // キャンセルの有無を判断
      if (isCancel()) {
        // キャンセルのため処理を抜ける
        break;
      }
    }

    // responseの内容を保持
    await dispatch(setReserveResponse(results));

    // DryRunモードor通常モード且つエラーを含む場合、ダイアログ表示(DryRunモード且つエラーを含まない場合は、はい/いいえボタンがある確認ダイアログ)
    if (isDryRun || (!isDryRun && results.errorDevices.length > 0)) {
      await dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_UPLOAD_RESULT_DISP));
    }

    // 有償機能アクティベート情報取得APIを通常モードで実行(results.errorDevicesにデータが存在する場合はエラーダイアログ/results.errorDevicesにデータが存在しなくてもDrynRunモードの場合は確認ダイアログを閉じる処理で取得APIを実行しているためここでは実行しない)
    if (!isDryRun && results.errorDevices.length === 0) {
      // 最初に取得した有償機能アクティベート情報一覧の他、deviceActivateReducerで管理しているstateをクリア
      await dispatch(resetDevicesActivation());
      // 再取得
      await dispatch(
        getDevicesActivation(token, refreshToken, purchaseId, false, false)
      );
    }

    // 通常モードの時は購入履歴を再取得する＆処理を終了する(DryRunモード実行後に確認ダイアログの裏で一覧が表示されてしまうのを防ぐためDryRunモードの時は処理中のままにする)
    if (!isDryRun) {
      // POST_DEVICES_ACTIVATION_RESERVE_IDを未処理に変更
      dispatch(endFetch(POST_DEVICES_ACTIVATION_RESERVE_ID));
      dispatch(setIsProcessing(false));
      await dispatch(reset());
      await dispatch(
        getPurchasesHistory(
          token,
          language,
          PurchaseStateType.TYPE_ALL,
          SortType.TYPE_PURCHASE_ID,
          null
        )
      );
    }
  };
};

/**
 * 有償ファームウェアのアクティベート確定APIを実行する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 購入番号
 * @param glossary: グロッサリ
 * @param isRetry: 再実施か否か
 */
export const putDevicesActivationOutput = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  glossary: Glossary,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義 nextKeyで取得する場合は必要
    const isCancel = () => {
      const status = getState().app.fetch[GET_DEVICES_ACTIVATION_OUTPUT_ID];
      return status === FetchStatus.CANCEL;
    };
    // GET_DEVICES_ACTIVATION_OUTPUT_IDを処理中に変更
    dispatch(beginFetch(GET_DEVICES_ACTIVATION_OUTPUT_ID));
    dispatch(setIsProcessing(true));

    // ErrorStateが有償機能確定APIだった場合はリセットする
    if (getState().app.error.requestId === GET_DEVICES_ACTIVATION_OUTPUT_ID) {
      dispatch(clearErrors());
    }

    let nextKey = '';
    let actHeader: ActivationHeader = {
      company: '',
      description: '',
      actVersion: ''
    };
    let vendorPurchaseId = '';
    const actData: ActivationData[] = [];
    do {
      // 有償ファームウェアのアクティベート確定APIを実行
      const requestData = putDevicesActivationOutputRequest(
        token,
        purchaseId,
        nextKey
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = GET_DEVICES_ACTIVATION_OUTPUT_ID;
        // 403エラーの場合はトークン更新後に再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              await dispatch(
                putDevicesActivationOutput(
                  cookie[SasCookieKeys.KEY_TOKEN],
                  cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                  purchaseId,
                  glossary,
                  true
                )
              );
              // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも401or403だった場合はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403(needLogout)であった場合はSASのみログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(GET_DEVICES_ACTIVATION_OUTPUT_ID));
        dispatch(setIsProcessing(false));
        // エラーダイアログを表示
        dispatch(setApiErrorMessage(glossary.glx(BoardGL.DownloadFailure)));
        dispatch(
          setStatus(BoardStatus.DEVICE_ACTIVATE_API_ERROR_COMPLETION_DISP)
        );
        return;
      }
      // APIが成功した場合はACTデータを保持する
      const body: PutDevicesActivationOutputResponse = (response.body as unknown) as PutDevicesActivationOutputResponse;
      // ヘッダーデータを保持
      actHeader = body.header;
      // 決済ベンダーが発行している注文番号を保持
      vendorPurchaseId = body.vendorPurchaseId;
      for (const data of body.activations) {
        actData.push(data);
      }
      // nextKeyの有無、キャンセルの有無を判断
      if (body.nextKey === '' || isCancel()) {
        // キャンセル又は次の取得が無いため処理を抜ける
        nextKey = '';
      } else {
        // nextKeyを更新
        nextKey = body.nextKey;
      }
    } while (nextKey);

    // ACTファイルダウンロード
    try {
      await outputACTFile(
        actHeader,
        vendorPurchaseId,
        actData,
        dispatch,
        glossary
      );
    } catch {
      return;
    }
    // GET_DEVICES_ACTIVATION_OUTPUT_IDを未処理に変更
    dispatch(endFetch(GET_DEVICES_ACTIVATION_OUTPUT_ID));
    dispatch(setIsProcessing(false));
    // 最初に取得した有償機能アクティベート情報一覧をクリア
    dispatch(resetDevicesActivation());
    // 有償機能アクティベート情報取得APIを実行(401/403エラー以外となった場合に2秒毎に最大15回リトライする)
    dispatch(
      getDevicesActivation(token, refreshToken, purchaseId, false, true)
    );
  };
};

/**
 * 有償機能予約取り消しAPIを実行する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 購入番号
 * @param datas: 取り消し対象データリスト([{ modelId: string; serialNo: string }])
 * @param glossary: グロッサリ
 * @param isRetry: 再実施か否か
 */
export const postDevicesActivationReserveCancel = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  datas: { modelId: string; serialNo: string }[],
  glossary: Glossary,
  language: string,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義
    const isCancel = () => {
      const status = getState().app.fetch[
        POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID
      ];
      return status === FetchStatus.CANCEL;
    };
    // POST_DEVICES_ACTIVATION_RESERVE_CANCEL_IDを処理中に変更
    dispatch(beginFetch(POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID));
    dispatch(setIsProcessing(true));

    // ErrorStateが有償機能予約取り消しだった場合はリセットする
    if (
      getState().app.error.requestId ===
      POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID
    ) {
      dispatch(clearErrors());
    }
    for (const data of datas) {
      // 有償ファームウェアのアクティベート予約削除APIを実行
      const requestData = postDevicesActivationReserveCancelRequest(
        token,
        purchaseId,
        data.modelId,
        data.serialNo
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID;
        // 403の場合はトークン更新後に再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              await dispatch(
                postDevicesActivationReserveCancel(
                  cookie[SasCookieKeys.KEY_TOKEN],
                  cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                  purchaseId,
                  datas,
                  glossary,
                  language,
                  true
                )
              );
              // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも401or403だった場合はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403(needLogout)であった場合はSASのみログアウト状態にす
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID));
        dispatch(setIsProcessing(false));
        // エラーダイアログを表示
        dispatch(setApiErrorMessage(glossary.glx(BoardGL.DeleteFailure)));
        dispatch(
          setStatus(BoardStatus.DEVICE_ACTIVATE_API_ERROR_COMPLETION_DISP)
        );
        return;
      }
      // キャンセルの有無を判断
      if (isCancel()) {
        // キャンセルのため処理を抜ける
        break;
      }
    }

    // POST_DEVICES_ACTIVATION_RESERVE_CANCEL_IDを未処理に変更
    dispatch(endFetch(POST_DEVICES_ACTIVATION_RESERVE_CANCEL_ID));
    dispatch(setIsProcessing(false));
    // 有償機能アクティベート情報取得APIを実行
    await dispatch(
      getDevicesActivation(token, refreshToken, purchaseId, false, false)
    );
    // 購入履歴情報を再取得する
    await dispatch(reset());
    await dispatch(
      getPurchasesHistory(
        token,
        language,
        PurchaseStateType.TYPE_ALL,
        SortType.TYPE_PURCHASE_ID,
        null
      )
    );
  };
};

/**
 * 有償機能アクティベート情報をクリアする
 */
export const resetDevicesActivation = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * 有償機能アクティベート情報
 */
export interface DeviceActivateInformation {
  /** 状態("active"：有効 or "reserve"：予約) */
  status: StateType;
  /** 機種ID */
  modelId: string;
  /** モデル名 */
  modelName: string;
  /** シリアル番号 */
  serialNo: string;
  /** アップロード日時 (ISO8601 UTC) */
  uploadAt: string;
}

/**
 * 有償機能アクティベート情報マップ
 * キーに端末ID(機種ID+シリアル番号)、値に有償機能アクティベート情報、削除チェックボックスの状態を持つ
 * {
 *   端末ID: {
 *     deviceActivateInfo: 有償機能アクティベート情報,
 *     isDelete: 削除チェックボックスの状態(true or false)
 *   }
 * }
 */
export interface DeviceActivateMap {
  [key: string]: {
    deviceActivateInfo: DeviceActivateInformation;
    isDelete: boolean;
  };
}

/**
 * 有償機能情報
 * {
 *   usage: 利用数
 *   limit: 利用上限数
 *   confirmAt: ACTファイル出力日時(ISO8601UTC)
 *   activates: {
 *     端末ID: 有償機能アクティベート情報マップ
 *   }
 * }
 */
export interface PaidFeaturesInfo {
  usage: number;
  limit: number;
  confirmAt: string;
  activates: DeviceActivateMap;
}

/**
 * 有償機能アクティベート情報のState
 * キーにユーザー購入番号、値に有償機能情報を持つ
 * {
 *   ユーザー購入番号: 有償機能情報
 * }
 */
export interface DeviceActivateState {
  [key: string]: PaidFeaturesInfo;
}

/**
 * リクエストファイル読み込み時のエラー情報
 */
export interface FileLoadingErrorInfo {
  fileName: string;
  message: string;
}

export interface DeviceActivateInitialState {
  /** 機能アクティベート管理画面に表示するデータ */
  deviceActivateMap: DeviceActivateState;
  /** 読み込んだリクエストファイルのデータ */
  reqDataList: [];
  /** 予約APIの実行して200で返ってきたときのレスポンス情報 */
  reserveResponse: PostDevicesActivationReserveResponse;
  /** リクエストファイル読み込み時のエラー情報 */
  fileLoadingErrorData: FileLoadingErrorInfo[];
  /** 予約APIor確定APIがエラーで返ってきたときのエラーメッセージ */
  apiErrorMessage: string;
  /** ダイアログのモード */
  dialogMode: DialogType;
}

const initialState: DeviceActivateInitialState = {
  deviceActivateMap: {},
  reqDataList: [],
  reserveResponse: { successDevices: [], warningDevices: [], errorDevices: [] },
  fileLoadingErrorData: [],
  apiErrorMessage: '',
  dialogMode: DialogType.NORMAL
};

export default (
  state: DeviceActivateInitialState = initialState,
  actions: TerminalAction
): DeviceActivateInitialState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS: {
      const modifiedState = { ...state.deviceActivateMap };
      // 更新対象のユーザー購入番号を取得
      const up_keys = Object.keys(actions.payload);
      for (const key of up_keys) {
        // 既に同じユーザー購入番号のデータを更新
        modifiedState[key] = { ...modifiedState[key], ...actions.payload[key] };
      }
      return { ...state, deviceActivateMap: modifiedState };
    }
    case ActionTypes.RESET_STATE: {
      return initialState;
    }
    case ActionTypes.CHANGE_SETTING: {
      const modifiedState = { ...state.deviceActivateMap };
      // 対象データを取り出し、isDeleteを変更する
      const purchaseId = actions.payload.purchaseId;
      const terminalId = actions.payload.terminalId;
      modifiedState[purchaseId].activates[terminalId].isDelete =
        actions.payload.isDelete;
      return { ...state, deviceActivateMap: modifiedState };
    }
    case ActionTypes.SET_REQ_DATA_LIST: {
      return { ...state, reqDataList: actions.payload };
    }
    case ActionTypes.SET_RESERVE_RESPONSE: {
      return { ...state, reserveResponse: actions.payload };
    }
    case ActionTypes.SET_FILE_LOADING_ERROR_DATA: {
      return { ...state, fileLoadingErrorData: actions.payload };
    }
    case ActionTypes.SET_API_ERROR_MESSAGE: {
      return { ...state, apiErrorMessage: actions.payload };
    }
    case ActionTypes.SET_DIALOG_MODE: {
      return { ...state, dialogMode: actions.payload };
    }
    default: {
      return state;
    }
  }
};
